/**
 * PlaylistsHomePage Component
 * Displays user's playlists (owned and shared) and allows creating new ones
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPlaylists, type Playlist } from '../api';
import CreatePlaylistModal from './CreatePlaylistModal';
import PlaylistStatusBadge from './PlaylistStatusBadge';

export default function PlaylistsHomePage() {
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [sharedPlaylists, setSharedPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const loadPlaylists = async () => {
    try {
      const data = await getPlaylists();
      setPlaylists(data.playlists);
      setSharedPlaylists(data.shared_playlists);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load playlists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlaylists();
  }, []);

  const handlePlaylistCreated = (playlist: Playlist) => {
    setPlaylists((prev) => [playlist, ...prev]);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Separate system playlists from regular ones
  const systemPlaylists = playlists.filter((p) => p.type !== null);
  const regularPlaylists = playlists.filter((p) => p.type === null);

  // Sort system playlists: want_to_watch first, then watched
  const sortedSystemPlaylists = [...systemPlaylists].sort((a, b) => {
    if (a.type === 'want_to_watch') return -1;
    if (b.type === 'want_to_watch') return 1;
    return 0;
  });

  const getSystemPlaylistIcon = (type: string | null) => {
    if (type === 'want_to_watch') {
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="20"
          height="20"
        >
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      );
    }
    if (type === 'watched') {
      return (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          width="20"
          height="20"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    }
    return null;
  };

  return (
    <>
      <div className="playlists-header">
        <div>
          <h2>Your Playlists</h2>
          <p>Create movie playlists using natural language prompts</p>
        </div>
        <button onClick={() => setShowCreateModal(true)}>+ New Playlist</button>
      </div>

      {loading ? (
        <div className="empty">Loading playlists...</div>
      ) : error ? (
        <div className="empty">Error: {error}</div>
      ) : playlists.length === 0 && sharedPlaylists.length === 0 ? (
        <div className="empty-state">
          <h3>No playlists yet</h3>
          <p>Create your first playlist by describing the movies you want to watch.</p>
          <button onClick={() => setShowCreateModal(true)}>Create Your First Playlist</button>
        </div>
      ) : (
        <>
          {/* System Playlists (Want to Watch + Watched) */}
          {sortedSystemPlaylists.length > 0 && (
            <div className="system-playlists">
              {sortedSystemPlaylists.map((playlist) => (
                <Link
                  key={playlist.id}
                  to={`/playlists/${playlist.id}`}
                  className={`system-playlist-card system-playlist-${playlist.type}`}
                >
                  <div className="system-playlist-icon">{getSystemPlaylistIcon(playlist.type)}</div>
                  <div className="system-playlist-info">
                    <h3>{playlist.name}</h3>
                    <span className="playlist-count">{playlist.movie_count ?? 0} movies</span>
                  </div>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    width="16"
                    height="16"
                    className="system-playlist-arrow"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </Link>
              ))}
            </div>
          )}

          {/* My Playlists */}
          {regularPlaylists.length > 0 && (
            <div className="playlists-grid">
              {regularPlaylists.map((playlist) => (
                <Link key={playlist.id} to={`/playlists/${playlist.id}`} className="playlist-card">
                  <div className="playlist-card-header">
                    <h3>{playlist.name}</h3>
                    {playlist.status !== 'completed' && (
                      <PlaylistStatusBadge
                        status={playlist.status}
                        errorMessage={playlist.error_message}
                      />
                    )}
                  </div>
                  <p className="playlist-prompt">{playlist.prompt}</p>
                  <div className="playlist-card-footer">
                    <span className="playlist-date">{formatDate(playlist.created_at)}</span>
                    {playlist.status === 'completed' && (
                      <span className="playlist-count">
                        {playlist.movie_count ?? playlist.progress_approved} movies
                      </span>
                    )}
                    {playlist.status === 'processing' && (
                      <span className="playlist-progress">
                        {playlist.progress_approved} / {playlist.progress_found} movies
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}

          {regularPlaylists.length === 0 && (
            <div className="empty-state small">
              <p>You haven't created any playlists yet.</p>
              <button onClick={() => setShowCreateModal(true)}>Create Your First Playlist</button>
            </div>
          )}

          {/* Shared with me */}
          {sharedPlaylists.length > 0 && (
            <>
              <div className="playlists-header shared-header">
                <div>
                  <h2>Shared with Me</h2>
                  <p>Playlists your friends have shared</p>
                </div>
              </div>
              <div className="playlists-grid">
                {sharedPlaylists.map((playlist) => (
                  <Link
                    key={playlist.id}
                    to={`/playlists/${playlist.id}`}
                    className="playlist-card shared"
                  >
                    <div className="playlist-card-header">
                      <h3>{playlist.name}</h3>
                      <span
                        className={`permission-badge ${playlist.permission === 'edit' ? 'edit' : ''}`}
                      >
                        {playlist.permission === 'edit' ? 'Can edit' : 'View only'}
                      </span>
                    </div>
                    <p className="playlist-prompt">{playlist.prompt}</p>
                    <div className="playlist-card-footer">
                      <span className="shared-by">
                        by @{playlist.shared_by?.username || playlist.shared_by?.name || 'Unknown'}
                      </span>
                      {playlist.status === 'completed' && (
                        <span className="playlist-count">
                          {playlist.movie_count ?? playlist.progress_approved} movies
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <CreatePlaylistModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handlePlaylistCreated}
      />
    </>
  );
}
